import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import { Apis } from "../../../lib/Apis";
import Loader from "../../../Utils/loader";
import { usePermission, SystemPermissions } from "../../../Utils/permissionAccess";

function formatMoney(v) {
  if (v == null || v === "") return "—";
  const n = Number(v);
  return Number.isFinite(n) ? `Rs. ${n.toLocaleString()}` : String(v);
}

export default function PurchasedHouseDesignDetail() {
  const { purchaseId } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = usePermission();
  const canViewDetail = hasPermission(SystemPermissions.PurchasedHouseDesigns.VIEW_DETAIL);

  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!canViewDetail) {
      navigate("/unauthorized");
      return;
    }

    let cancelled = false;
    (async () => {
      if (!purchaseId) return;
      setLoading(true);
      try {
        const response = await Apis.getPurchasedHouseDesignById(purchaseId);
        const body = response?.data;
        const detailPayload =
          body &&
          typeof body === "object" &&
          "data" in body &&
          ("success" in body || "statusCode" in body)
            ? body.data
            : body?.data ?? body;
        if (!cancelled) setDetail(detailPayload);
      } catch (e) {
        console.error(e);
        if (!cancelled) {
          Swal.fire({
            icon: "error",
            title: "Could not load",
            text: "Purchase not found or you may not have access.",
          });
          navigate("/purchased-house-designs");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [purchaseId, navigate, canViewDetail]);

  if (!canViewDetail) {
    return null;
  }

  if (loading || !detail) {
    return <Loader message="Loading purchase details..." />;
  }

  const user = detail.user || {};
  const hd = detail.house_design || {};
  const info = detail.user_information || {};
  const shotUrl = detail.payment_screenshot_url || null;

  const paymentLabel = () => {
    if (detail.payment_status === "completed" && detail.payment_approved) {
      return { text: "Payment approved", className: "btn btn-action pointer-events-none" };
    }
    if (detail.payment_status === "failed") {
      return { text: "Payment declined", className: "btn btn-delete text-white pointer-events-none" };
    }
    if (detail.payment_status === "pending" && detail.payment_screenshot) {
      return { text: "Awaiting admin verification", className: "btn btn-view text-white pointer-events-none" };
    }
    return {
      text: "Awaiting payment screenshot",
      className: "btn btn-reset-filter pointer-events-none",
    };
  };

  const badge = paymentLabel();

  const drawings = Array.isArray(detail.selected_drawings)
    ? detail.selected_drawings
    : [];

  return (
    <div>
      <div className="bg-gray-100 p-4 flex flex-wrap justify-between items-center gap-3">
        <div>
          <h2 className="text-2xl font-bold mb-1">Purchased House Design Details</h2>
          <p className="text-sm text-gray-500">
            Purchase #{detail.id}
            {detail.created_at
              ? ` · ${new Date(detail.created_at).toLocaleString()}`
              : ""}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <span className={badge.className}>{badge.text}</span>
        </div>
      </div>

      <div className="max-w-5xl mx-auto my-6 p-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <section className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 border-b pb-2 mb-4">
              Customer (account)
            </h3>
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between gap-4">
                <dt className="text-gray-500">Name</dt>
                <dd className="font-medium text-right">
                  {user.firstName} {user.lastName}
                </dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="text-gray-500">Email</dt>
                <dd className="text-right break-all">{user.email || "—"}</dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="text-gray-500">Phone</dt>
                <dd className="text-right">{user.contactNo || "—"}</dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="text-gray-500">City</dt>
                <dd className="text-right">{user.city || "—"}</dd>
              </div>
            </dl>
          </section>

          <section className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 border-b pb-2 mb-4">
              Billing / contact (at purchase)
            </h3>
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between gap-4">
                <dt className="text-gray-500">Name</dt>
                <dd className="font-medium text-right">{info.name || "—"}</dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="text-gray-500">Contact</dt>
                <dd className="text-right">{info.contactNo || "—"}</dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="text-gray-500">Address</dt>
                <dd className="text-right max-w-[70%]">{info.address || "—"}</dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="text-gray-500">City</dt>
                <dd className="text-right">{info.city || "—"}</dd>
              </div>
            </dl>
          </section>

          <section className="bg-white rounded-lg shadow p-6 lg:col-span-2">
            <h3 className="text-lg font-semibold text-gray-900 border-b pb-2 mb-4">
              House design
            </h3>
            <h4 className="text-xl font-bold text-gray-900 mb-2">{hd.title || "—"}</h4>
            <div className="grid sm:grid-cols-2 gap-3 text-sm mb-4">
              <div>
                <span className="text-gray-500">Plot</span>{" "}
                <span className="font-medium">{hd.plot_dimension || "—"}</span>
              </div>
              <div>
                <span className="text-gray-500">Covered area</span>{" "}
                <span className="font-medium">{hd.covered_area || "—"}</span>
              </div>
              <div>
                <span className="text-gray-500">Floors</span>{" "}
                <span className="font-medium">{hd.floors ?? "—"}</span>
              </div>
              <div>
                <span className="text-gray-500">Beds / baths</span>{" "}
                <span className="font-medium">
                  {hd.bed_rooms ?? "—"} / {hd.bath_rooms ?? "—"}
                </span>
              </div>
              {hd.customUrl && (
                <div className="sm:col-span-2">
                  <span className="text-gray-500">URL slug</span>{" "}
                  <span className="font-mono text-xs">{hd.customUrl}</span>
                </div>
              )}
            </div>
            {hd.description && (
              <div
                className="prose prose-sm max-w-none text-gray-700 border-t pt-4"
                dangerouslySetInnerHTML={{ __html: hd.description }}
              />
            )}
          </section>

          <section className="bg-white rounded-lg shadow p-6 lg:col-span-2">
            <h3 className="text-lg font-semibold text-gray-900 border-b pb-2 mb-4">
              Order details
            </h3>
            <dl className="grid sm:grid-cols-2 gap-4 text-sm">
              <div>
                <dt className="text-gray-500">Total price</dt>
                <dd className="font-semibold text-lg">{formatMoney(detail.price)}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Design label</dt>
                <dd className="break-words">{detail.design_id || "—"}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Garage</dt>
                <dd className="capitalize">{detail.garage_location || "—"}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Payment status</dt>
                <dd className="capitalize">{detail.payment_status || "—"}</dd>
              </div>
            </dl>

            {drawings.length > 0 && (
              <div className="mt-6 overflow-x-auto">
                <h4 className="font-medium text-gray-900 mb-2">Selected drawings</h4>
                <table className="min-w-full bg-white shadow-md rounded-lg overflow-hidden">
                  <thead>
                    <tr className="brand-gradient text-white">
                      <th className="table-th text-left">Drawing</th>
                      <th className="table-th text-left">Price</th>
                      <th className="table-th text-left">Plan</th>
                    </tr>
                  </thead>
                  <tbody>
                    {drawings.map((d, i) => (
                      <tr key={i}>
                        <td className="table-td">
                          {d.selectedDrawings || d.label || "Item"}
                        </td>
                        <td className="table-td">{formatMoney(d.price)}</td>
                        <td className="table-td">
                          {d.buyPlanId != null ? `#${d.buyPlanId}` : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {shotUrl && (
              <div className="mt-6">
                <h4 className="font-medium text-gray-900 mb-3">Payment screenshot</h4>
                <a
                  href={shotUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="btn btn-view text-white justify-center inline-flex mb-3"
                >
                  Open image
                </a>
                <div className="rounded-lg overflow-hidden border max-w-md">
                  <img
                    src={shotUrl}
                    alt="Payment"
                    className="w-full h-auto object-contain max-h-80 bg-gray-50"
                  />
                </div>
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
